import { useEffect, useState, FormEvent, MouseEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import Table from "@/common/components/Table";
import { ORDERS_URL } from "@/common/constants";
import { Button, H4 } from "@/common/elements";
import useAxiosCall from "@/hooks/useAxiosCall";
import { RequisitionWrapper } from "./subs/style";
import { getTableHeaders, getTableKeys, OrderFilterOptions } from "./subs/data";
import { KeyValueObj } from "@/common/types";
import useAuth from "@/hooks/useContexts";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import Tab from "@/common/components/Tab/Tab";
import { useAuthDetails } from "@/hooks/useAuthDetails";
import { Pagination } from "@/common/components/pagination";
import { usePositioning } from "@/hooks/usePositioning";
import { PopupWrapper } from "@/common/elements/containers";
import { Modal } from "@/common/components/modal";
import { ActionPopup } from "@/common/components/action-popup";
import {
  formatKey,
  formatToNaira,
  getAddress,
  toParamsFormat,
} from "@/common/utils/general";
import { PageTitleNavs } from "@/common/components/page-title-navs";
import { RespondViewRfq } from "../rfq/respond-rfq/respond-view-rfq";
import { BatchSelector } from "./subs/batch-selector";
import {
  IAddedBatch,
  IBatchData,
  IProductBatches,
} from "@/common/types/others";
import { ActionEnquiry } from "./subs/action-enquiry";

import OrderByProductSearch from "./orderByProductSearch";
import { Search } from "../products/subs/styles";
import useQuery from "@/hooks/useQuery";
import { AddressManager } from "@/common/components/addresses";

export interface IRequisitionProps {}

interface ActionFormData {
  status: string;
  reason_for_rejection?: string;
  estimated_delivery_date?: string;
}

export interface IFormData {
  logistics_company: string[];
  estimated_delivery_date_range: string;
  recipent_phone_number: string;
  pickup_option: string;
  sender_phone_number: string;
  sender_address_type: string;
  vehicle_needed: string;
  category: string;
  min_temperature: number;
  max_temperature: number;
  length: number;
  width: number;
  height: number;
  package_type: string;
}

export function Orders(_: IRequisitionProps) {
  const [showActions, setShowActions] = useState(false);
  const [actions, setActions] = useState<string[]>([]);
  const [tableData, setTableData] = useState<KeyValueObj[]>([]);
  const [orderId, setOrderId] = useState("");
  const [orders, setOrders] = useState<KeyValueObj[]>([]);
  const [retry, setRetry] = useState(0);
  const [loading, setLoading] = useState(false);
  const [orderType, setOrderType] = useState("received");
  const [filterOrderType, setFilterOrderType] = useState("received");
  const [, setNoInboundOrderMessage] = useState(
    "No incoming orders placed yet, incoming orders will show here when you have one.",
  );
  const [, setNoOutboundOrderMessage] = useState(
    "You have not placed any order yet, orders you placed will show once.",
  );

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [pageCount, setPagCount] = useState(1);
  const [showActionEnquiry, setShowActionEnquiry] = useState(false);
  const [enquiryType, setEnquiryType] = useState("");
  const [enquiryPrompt, setEnquiryPrompt] = useState("");
  const [enquiryLoading, setEnquiryLoading] = useState(false);
  const [enquiryText, setEnquiryText] = useState("");
  const [showEnquiryText, setShowEnquiryText] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<KeyValueObj>({});
  const [showOrder, setShowOrder] = useState(false);
  const [subTotal, setSubTotal] = useState(0);
  const [productBatches, setProductBatches] = useState<IProductBatches>({});
  const [currentProductBatches, setCurrentProductBatches] = useState<
    IBatchData[]
  >([]);
  const [showBatchAdd, setShowBatchAdd] = useState(false);
  const [currentOrderProduct, setCurrentOrderProduct] = useState<KeyValueObj>(
    {},
  );
  const [retryingNotification, setRetryingNotification] = useState(false);

  const tabs = ["order by manufacturer", "all products"];
  const [ProductOrderTypes, setProductOrderType] = useState(
    "order by manufacturer",
  );

  useEffect(() => {
    void productBatches;
    void setCurrentProductBatches;
    void setCurrentOrderProduct;
  }, [productBatches, setCurrentProductBatches, setCurrentOrderProduct]);
  const [fetchReqs, fetchedReqs, reqsError, reqsLoading, meta] = useAxiosCall();
  const { auth } = useAuth();
  const apiCall = useApiCallHandling();
  const { companyType } = useAuthDetails();
  const [searching, setSearching] = useState(false);
  const [activeSelection, setActiveSelection] = useState<string | null>(null);
  const [setCordinate, cordinate] = usePositioning();
  const [requestDate, setRquestDate] = useState("");
  const navigate = useNavigate();

  function dotClicked(e: any, rowId: string) {
    setCordinate(e);
    setOrderId(rowId);

    const selectedReq = orders.find(
      (req: KeyValueObj) => req.id === rowId,
    ) as KeyValueObj;
    const orderStatus = selectedReq.order_status;
    const deliveryStatus = selectedReq.delivery_status;
    const paymentStatus = selectedReq.payment_status;
    const deliveryRequestData = selectedReq.delivery_requests;
    const DataDate = selectedReq.updated_at;
    setRquestDate(DataDate);

    if (orderType === "sent") {
      if (deliveryStatus === "pending" && orderStatus !== "rejected") {
        setActions(["View", "Cancel"]);
      } else if (deliveryStatus === "shipped") {
        setActions(["Goods Received"]);
      } else {
        setActions(["View"]);
      }
    } else if (orderType === "received") {
      switch (orderStatus) {
        case "draft":
          setActions(["View", "Accept", "Reject"]);
          break;
        case "accepted":
          if (deliveryStatus === "pending") setActions(["View", "Cancel"]);
          else if (deliveryStatus === "shipped") setActions(["View", "Cancel"]);
          else if (deliveryStatus === "received") setActions(["View"]);
          break;
        case "processed":
          if (paymentStatus === "paid" && !deliveryRequestData?.length)
            setActions(["View", "Ship", "Cancel"]);
          else setActions(["View"]);
          break;
        default:
          setActions(["View"]);
      }
    }

    setShowActions(true);
  }

  function setTableLoading(flag: boolean) {
    const index = tableData.findIndex((data: any) => data.id === orderId);
    const tableRow = tableData[index];
    tableRow.action = { ...tableRow.action, buttonLoading: flag };
    const tempTable = [...tableData];
    tempTable[index] = tableRow;
    setTableData(tempTable);
  }

  function handleShip() {
    navigate(
      `/dashboard/deliveries/auto-delivery-request/${orderId}?from=order`,
    );
  }

  function onActionClicked(action: string) {
    const currentOrderInner = orders.find(
      (order: KeyValueObj) => order.id === orderId,
    );
    setCurrentOrder(currentOrderInner ?? {});
    let subtotal = 0;
    currentOrderInner?.order_products?.forEach((product: KeyValueObj) => {
      subtotal += product?.price_per_unit * product?.quantity;
    });

    if (action?.toLowerCase() === "view") {
      setSubTotal(subtotal);
      setShowOrder(true);
      setShowActions(false);
      return;
    }

    setTableLoading(true);
    setLoading(true);

    let url: string = "";
    let formData: ActionFormData = { status: "" };
    switch (action) {
      case "Accept":
        url = ORDERS_URL + "/assess/" + orderId;
        formData = { status: "accept" };
        setActiveSelection("Accept");
        break;

      case "Reject":
        setTableLoading(false);
        setLoading(false);
        setActiveSelection("Reject");
        setEnquiryPrompt(
          "Are you sure you want to reject this Order? if yes, kindly state reason below",
        );
        setEnquiryType("reason");
        setShowActionEnquiry(true);

        break;

      case "Cancel":
        url = ORDERS_URL + "/assess/" + orderId;
        formData = { status: "reject" };
        break;

      case "Ship":
        handleShip();
        break;

      case "Goods Received":
        url = ORDERS_URL + "/received/" + orderId;
        formData = { status: "delivered" };
        break;
      default:
        url = "";
    }

    action !== "Reject" && action !== "Ship" && handleActionCall(url, formData);
  }

  async function handleActionCall(url: string, formData: ActionFormData) {
    setLoading(true);
    await apiCall(
      url,
      "POST",
      formData,
      (data: any) => {
        const gottenData = data.order ? data.order : data;
        const index = orders.findIndex(
          (order: KeyValueObj) => order.id === gottenData.id,
        );
        setOrders((prev: KeyValueObj[]) => {
          const copy = [...prev];
          copy[index] = gottenData;
          return copy;
        });
        setShowActions(false);
        setShowOrder(false);
      },
      (error: any) => {
        console.log(error);
      },
      () => {
        setTableLoading(false);
        setLoading(false);
        setEnquiryLoading(false);
      },
    );
  }

  async function onEnquiryContinue(text: string) {
    setEnquiryLoading(true);
    let url = "";
    let formData: ActionFormData = { status: "" };

    if (enquiryType === "reason") {
      url = ORDERS_URL + "/assess/" + orderId;
      formData = { status: "reject", reason_for_rejection: text };
    } else if (enquiryType === "date") {
      url = ORDERS_URL + "/shipped/" + orderId;
      formData = { status: "shipped", estimated_delivery_date: text };
    }

    await handleActionCall(url, formData);
    setShowActionEnquiry(false);
    setEnquiryLoading(false);
  }

  function updateTableData(queryParams: string) {
    setSearching(true);
    apiCall(
      `${ORDERS_URL}/${filterOrderType}${queryParams}`,
      "GET",
      {},
      (data: any, metaData: any) => {
        setOrders(data);
        typeof metaData.total_pages === "number" &&
          setPagCount(metaData.total_pages);
        if (data.length === 0)
          setNoOutboundOrderMessage(
            "The filter or search term you entered returned no data!",
          );
        setNoInboundOrderMessage(
          "The filter or search term you entered returned no data!",
        );
      },
      (err) => console.log(err),
      () => setSearching(false),
      { dontNotifyOnSucess: true, dontNotifyonFail: true },
    );
  }

  function handleSearch(value: string) {
    let values = { page, perPage, search_string: value };
    let params = toParamsFormat(values);
    updateTableData(params);
  }

  function handlOrderFilter(values: KeyValueObj) {
    values["page"] = page;
    values["per_page"] = perPage;
    const params = toParamsFormat(values);
    updateTableData(params);
  }
  function onMouseLeaveTablStatus() {
    setShowEnquiryText(false);
  }

  function onMouseEnterTableStatus(e: MouseEvent<HTMLElement>, rowId: string) {
    const order = orders.find((order: KeyValueObj) => order.id === rowId);
    setCordinate(e);
    if (order?.order_status === "rejected") {
      const rejectionReason = order?.reason_for_rejection;
      rejectionReason
        ? setEnquiryText(rejectionReason)
        : setEnquiryText("No reason was given for this rejection");
      setShowEnquiryText(true);
    } else {
      if (order?.delivery_status === "shipped") {
        const estimatedDeliveryDate = order?.estimated_delivery_date_range;
        estimatedDeliveryDate
          ? setEnquiryText(
              "Estimated delivery date: " +
                estimatedDeliveryDate.split("...").join(" to "),
            )
          : setEnquiryText("No estimated delivery date");
        setShowEnquiryText(true);
      }
    }
  }

  useEffect(() => {
    if (companyType?.toLowerCase() === "retailer") setOrderType("sent");
    if (companyType?.toLowerCase() === "retailer")
      setFilterOrderType("outbound");
    if (companyType?.toLowerCase() === "distributor")
      setFilterOrderType("outbound");
    if (companyType?.toLowerCase() === "manufacturer")
      setFilterOrderType("inbound");
  }, [companyType]);

  useEffect(() => {
    fetchReqs({
      method: "GET",
      url:
        ORDERS_URL +
        `/${
          orderType === "sent" ? "outbound" : "inbound"
        }?page=${page}&per_page=${perPage}`,
    });
  }, [auth, retry, orderType, perPage, page]);

  useEffect(() => {
    setOrders(fetchedReqs);
    typeof meta.total_pages === "number" && setPagCount(meta.total_pages);
  }, [fetchedReqs, meta.total_pages, orderType]);

  useEffect(() => {
    const tempData = orders?.map((req: KeyValueObj) => ({
      id: req.id,
      date: {
        type: "string",
        value: req.expected_delivery_date,
      },
      deliveryAddress: {
        type: "string",
        value: getAddress(req?.address),
      },
      paymentTerm: {
        type: "string",
        value: req?.payment_term,
      },
      client: {
        type: "string",
        value:
          orderType === "sent" ? req.distributor_info?.name : req?.owner?.name,
      },
      supplier: {
        type: "string",
        value:
          orderType === "sent" ? req.manufacturer_info?.name : req?.owner?.name,
      },
      callbackStatus: {
        type: "string",
        value: formatKey(req?.external_inventory_callback_status),
      },
      status: {
        type: "status",
        statusType:
          req.delivery_status === "pending"
            ? req.order_status === "draft"
              ? "pending"
              : req.order_status
            : req.delivery_status,
        value:
          req.delivery_status === "pending"
            ? req.order_status === "draft"
              ? "pending"
              : req.order_status
            : req.delivery_status,
        onMouseInnerEnter: onMouseEnterTableStatus,
        onMouseInnerLeave: onMouseLeaveTablStatus,
      },
      action: {
        type: "button",
        buttonType: "dots",
        buttonLoading: false,
        buttonName: "",
        onClick: dotClicked,
      },
    }));

    setTableData(tempData);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, companyType]);

  function handleNextPage(event: { selected: number }) {
    setPage(event.selected + 1);
  }

  function handlePerPage(event: FormEvent<HTMLInputElement>) {
    const { value } = event.currentTarget;
    value.length > 0 && setPerPage(+value);
  }

  function handleAddBatchContinue(
    addedBatches: IAddedBatch[],
    productId: string,
  ) {
    if (addedBatches.length > 0) {
      setProductBatches((prev: IProductBatches) => ({
        ...prev,
        [productId]: addedBatches,
      }));
    }
    setShowBatchAdd(false);
  }

  function onRetryNotificationSuccess() {
    setRetry((prev: number) => prev + 1);
    setShowOrder(false);
  }

  function retryNotification() {
    setRetryingNotification(true);
    apiCall(
      ORDERS_URL + `/${currentOrder.id}/retry-notification`,
      "PATCH",
      {},
      onRetryNotificationSuccess,
      console.log,
      () => setRetryingNotification(false),
    );
  }

  const [productPage, setProductPage] = useState(1);
  const [productPerPage, setProductPerPage] = useState(25);
  const [productPageCount, setProductPageCount] = useState(1);

  const {
    data: inStockProducts,
    loading: inStockLoading,
    meta: inStockMeta,
  } = useQuery(
    `/api/v1/all-products?page=${productPage}&per_page=${productPerPage}`,
  );

  useEffect(() => {
    typeof inStockMeta.total_pages === "number" &&
      setProductPageCount(inStockMeta.total_pages);
  }, [inStockMeta]);

  function handleProductNextPage(event: { selected: number }) {
    setProductPage(event.selected + 1);
  }

  function handleProductPerPage(event: FormEvent<HTMLInputElement>) {
    const { value } = event.currentTarget;
    value.length > 0 && setProductPerPage(+value);
  }

  const [searchTerm, setSearchTerm] = useState<any | string>("");
  const [selectedProduct, setSelectedProduct] = useState<{
    id: string;
    name: string;
    company: string;
    batchNo: string;
    expiryDate: string;
    available: number;
    imageUrl: string;
    status: "available" | "out-of-stock" | "low-stock";
    unitPrice: string;
    packPrice: string;
    cartonPrice: string;
    unitsPerPack: number;
    packsPerCarton: number;
  } | null>(null);
  const [showProductPreview, setShowProductPreview] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<KeyValueObj>({});
  const [selectedRawItem, setSelectedRawItem] = useState<any>(null);
  const [orderQuantity, setOrderQuantity] = useState("");
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState("");
  const [orderNote, setOrderNote] = useState("");
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const products = ((inStockProducts as any[]) || []).map((item: any) => {
    const batches: any[] = item?.batches ?? [];
    const totalQty = batches.reduce(
      (sum: number, b: any) => sum + (b?.quantity_in_stock ?? 0),
      0,
    );
    const pricedBatch =
      batches.find((b: any) => parseFloat(b?.selling_price_per_unit) > 0) ??
      batches[0] ??
      {};
    const unitPriceRaw = parseFloat(pricedBatch?.selling_price_per_unit ?? "0");
    const unitsPerPack: number =
      item?.primary_package_quantity_per_secondary_package ?? 0;
    const packsPerCarton: number =
      item?.secondary_package_quantity_per_tertiary_package ?? 0;
    return {
      id: item?.id ?? "",
      name: item?.brand_name ?? "",
      company: item?.owner?.name ?? "",
      batchNo:
        batches.length > 1
          ? `${batches.length} batches`
          : (batches[0]?.batch_number ?? ""),
      expiryDate: "",
      available: totalQty,
      imageUrl: item?.images?.[0]?.url ?? "",
      status: "available" as const,
      unitPrice: formatToNaira(unitPriceRaw),
      packPrice: formatToNaira(unitPriceRaw * unitsPerPack),
      cartonPrice: formatToNaira(unitPriceRaw * unitsPerPack * packsPerCarton),
      unitsPerPack,
      packsPerCarton,
    };
  });

  function closeProductPreview() {
    setShowProductPreview(false);
    setSelectedProduct(null);
    setSelectedRawItem(null);
    setOrderQuantity("");
    setExpectedDeliveryDate("");
    setOrderNote("");
  }

  function handleProductOrder() {
    if (!selectedProduct || !selectedRawItem || !selectedAddress?.id) return;
    setSubmittingOrder(true);

    const qty = Number(orderQuantity);
    const batches = (selectedRawItem.batches ?? [])
      .filter((b: any) => b.quantity_in_stock > 0)
      .map((b: any) => ({ inventory_id: b.inventory_id, quantity: qty }));

    const payload = {
      shipping_address_id: selectedAddress.id,
      supplier_id: selectedRawItem.owner_id,
      expected_delivery_date: expectedDeliveryDate,
      buyer_note: orderNote,
      products: [
        {
          item_type: "drug",
          atc_code_id: selectedRawItem.atc_code?.id,
          brand_name: selectedRawItem.brand_name,
          quantity: qty,
          package_type: "",
          price_per_unit: 0,
          description: "",
          product_strength: selectedRawItem.strength,
          product_strength_unit: selectedRawItem.strength_unit,
          batches,
        },
      ],
    };

    apiCall(
      ORDERS_URL,
      "POST",
      payload,
      () => closeProductPreview(),
      (err: any) => console.log(err),
      () => setSubmittingOrder(false),
    );
  }

  return (
    <RequisitionWrapper>
      <PageTitleNavs
        title="Orders"
        backLink="#"
        bcrumbs={[{ title: "Orders", link: "#" }]}
      />

      <div>
        {["retailer", "distributor"].includes(companyType?.toLowerCase()) && (
          <Tab
            tabsTitle={tabs}
            onTabClick={(title: string) => setProductOrderType(title)}
            currentTab={ProductOrderTypes}
            paramKey="productOrderTab"
          />
        )}

        <div style={{ marginTop: "20px" }}>
          {ProductOrderTypes === "order by manufacturer" && (
            <>
              <Modal
                isOpen={showActionEnquiry}
                close={() => setShowActionEnquiry(false)}
              >
                <ActionEnquiry
                  enquiryType={enquiryType}
                  title={enquiryPrompt}
                  onCancel={() => setShowActionEnquiry(false)}
                  onContinue={onEnquiryContinue}
                  enquiryLoading={enquiryLoading}
                />
              </Modal>

              <Modal
                title="Select Batches"
                subTitle="Select a batch and the enter the quantity"
                isOpen={showBatchAdd}
                close={() => setShowBatchAdd(false)}
              >
                <BatchSelector
                  handleContinue={handleAddBatchContinue}
                  productData={currentProductBatches}
                  orderProduct={currentOrderProduct}
                />
              </Modal>
              {showEnquiryText && (
                <PopupWrapper position={cordinate}>
                  <div className="enquiry-text-wrapper">
                    <H4>{enquiryText}</H4>
                  </div>
                </PopupWrapper>
              )}

              <ActionPopup
                position={cordinate}
                open={showActions}
                disabled={loading}
                actions={actions.map((act: string) => ({
                  label: act,
                  action: () => onActionClicked(act),
                }))}
                onClose={() => setShowActions(false)}
              />

              <Modal isOpen={showOrder} close={() => setShowOrder(false)}>
                <div style={{ minWidth: "60rem", maxWidth: "95%" }}>
                  <RespondViewRfq
                    company={
                      currentOrder?.distributor_info ||
                      currentOrder?.manufacturer_info
                    }
                    rfq={{
                      quotation_request_products: currentOrder?.order_products,
                      quotation_request_id: "",
                      quotation_id: "",
                      payment_term: currentOrder?.payment_term,
                      shipping_address: currentOrder?.shipping_address,
                      owner: currentOrder?.owner,
                      external_inventory_callback_status:
                        currentOrder.external_inventory_callback_status,
                      retryingNotification: retryingNotification,
                    }}
                    logisticsCost={currentOrder?.delivery_charges}
                    tax={currentOrder?.tax}
                    discount={currentOrder?.discount}
                    total={currentOrder?.total_amount}
                    rowTotals={{}}
                    subTotal={subTotal}
                    terms={""}
                    handleSecondaryButton={() => {}}
                    handleMainButton={() => {}}
                    isViewing
                    viewer=""
                    quotation={currentOrder?.order_products}
                    quotationExpiry={currentOrder?.expected_delivery_date}
                    title="Order"
                    ordertype={orderType}
                    retryNotification={retryNotification}
                    orderNote={currentOrder?.buyer_note}
                    submit={onActionClicked}
                    status={currentOrder?.order_status}
                    loading={loading}
                    activeButton={activeSelection as string}
                    requestDate={requestDate}
                  />
                </div>
              </Modal>

              {companyType?.toLowerCase() === "distributor" && (
                <div className="button-wrapper">
                  <Button>
                    <Link
                      style={{ color: "inherit", textDecoration: "none" }}
                      to="/dashboard/requisition/make-requisition"
                    >
                      New Order
                    </Link>
                  </Button>
                </div>
              )}

              <div className="requisitions">
                {companyType?.toLowerCase() === "distributor" && (
                  <div className="tabs-wrapper">
                    <Tab
                      tabsTitle={["received", "sent"]}
                      onTabClick={(title: string) => setOrderType(title)}
                      currentTab={orderType}
                      paramKey="orderType"
                    />
                  </div>
                )}

                <div className="table-wrapper1">
                  <Table
                    tableKeys={getTableKeys(companyType)}
                    tableHeaders={getTableHeaders(orderType, companyType)}
                    tableData={tableData}
                    // tableData={[]}
                    full
                    spaced
                    onTDClick={() => {}}
                    filterInputs={OrderFilterOptions}
                    handleFilter={handlOrderFilter}
                    rowLineColor="light"
                    tableTitle="My Orders"
                    tableSubtitle={`${orderType} orders`}
                    handleSearch={handleSearch}
                    loading={reqsLoading || searching}
                    errorLoading={!!reqsError}
                    onRetry={() => setRetry((prev) => prev + 1)}
                  />
                </div>
              </div>
              <Pagination
                onNextPage={handleNextPage}
                onPerPageChange={handlePerPage}
                pageCount={pageCount}
              />
            </>
          )}

          {["retailer", "distributor"].includes(companyType?.toLowerCase()) &&
            ProductOrderTypes === "all products" && (
              <>
                <Modal isOpen={showProductPreview} close={closeProductPreview}>
                  <div className="p-6 w-[480px] max-w-full">
                    {selectedProduct &&
                      (() => {
                        const statusConfig = {
                          available: {
                            label: "Available",
                            badge:
                              "border-green-400 bg-green-50 text-green-500",
                            dot: "bg-green-500",
                            disabled: false,
                          },
                          "out-of-stock": {
                            label: "Out of stock",
                            badge: "border-red-400 bg-red-50 text-red-500",
                            dot: "bg-red-500",
                            disabled: true,
                          },
                          "low-stock": {
                            label: "Low stock",
                            badge:
                              "border-orange-400 bg-orange-50 text-orange-400",
                            dot: "bg-orange-400",
                            disabled: false,
                          },
                        };
                        const stock = statusConfig[selectedProduct.status];
                        return (
                          <>
                            <div className="flex gap-4 mb-5 pb-4 border-b border-neutral-100">
                              <img
                                src={selectedProduct.imageUrl}
                                alt={selectedProduct.name}
                                className="h-32 w-32 rounded object-cover shrink-0"
                              />
                              <div className="flex flex-col gap-2 justify-center min-w-0">
                                <div>
                                  <h2 className="text-base font-semibold text-neutral-800 truncate">
                                    {selectedProduct.name}
                                  </h2>
                                  <p className="text-[10px] font-medium uppercase tracking-wide text-neutral-500 mt-0.5">
                                    {selectedProduct.company}
                                  </p>
                                </div>
                                <span
                                  className={`inline-flex h-6 w-fit items-center gap-1.5 rounded-full border px-3 text-xs font-semibold ${stock.badge}`}
                                >
                                  <span
                                    className={`h-2 w-2 rounded-full ${stock.dot}`}
                                  />
                                  {stock.label}
                                </span>
                              </div>
                            </div>

                            <div className="grid grid-cols-3 gap-3 mb-4">
                              {[
                                {
                                  label: "Batch No",
                                  value: selectedProduct.batchNo,
                                },
                                {
                                  label: "Expiry Date",
                                  value: selectedProduct.expiryDate,
                                },
                                {
                                  label: "Available",
                                  value: `${selectedProduct.available} ${selectedProduct.available === 1 ? "Carton" : "Cartons"}`,
                                },
                              ].map(({ label, value }) => (
                                <div key={label}>
                                  <p className="text-xs text-neutral-500">
                                    {label}
                                  </p>
                                  <p className="text-sm font-medium text-neutral-800 mt-0.5">
                                    {value}
                                  </p>
                                </div>
                              ))}
                            </div>

                            <div className="grid grid-cols-3 rounded bg-violet-50/80 px-2 py-3 mb-4">
                              {[
                                {
                                  label: "Unit Price",
                                  value: selectedProduct.unitPrice,
                                },
                                {
                                  label: "Pack Price",
                                  value: selectedProduct.packPrice,
                                },
                                {
                                  label: "Carton Price",
                                  value: selectedProduct.cartonPrice,
                                },
                              ].map(({ label, value }, i) => (
                                <div
                                  key={label}
                                  className={`flex flex-col items-center justify-center px-2 py-2 text-center ${i > 0 ? "border-l border-violet-300" : ""}`}
                                >
                                  <p className="text-xs text-neutral-500">
                                    {label}
                                  </p>
                                  <p className="mt-1 text-sm font-bold text-neutral-950">
                                    {value}
                                  </p>
                                </div>
                              ))}
                            </div>

                            <div className="grid h-14 grid-cols-2 rounded border border-neutral-200 mb-6">
                              <div className="flex items-center justify-center text-xs font-semibold text-neutral-800">
                                {selectedProduct.unitsPerPack} Units / Pack
                              </div>
                              <div className="flex items-center justify-center border-l border-neutral-200 text-xs font-semibold text-neutral-800">
                                {selectedProduct.packsPerCarton} Packs / Carton
                              </div>
                            </div>

                            <div className="mb-5">
                              <p className="text-xs font-semibold text-neutral-600 mb-2 uppercase tracking-wide">
                                Delivery Address
                              </p>
                              <AddressManager
                                selectedAddress={selectedAddress}
                                setSelectedAddress={setSelectedAddress}
                              />
                            </div>

                            <div className="space-y-3 mb-5">
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs text-neutral-500 mb-1">
                                    Product Quantity
                                  </label>
                                  <input
                                    type="number"
                                    min="1"
                                    value={orderQuantity}
                                    onChange={(e) =>
                                      setOrderQuantity(e.target.value)
                                    }
                                    placeholder="Enter quantity"
                                    className="w-full rounded border border-neutral-200 px-3 py-2 text-sm text-neutral-800 outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-200"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-neutral-500 mb-1">
                                    Expected Delivery Date
                                  </label>
                                  <input
                                    type="date"
                                    value={expectedDeliveryDate}
                                    onChange={(e) =>
                                      setExpectedDeliveryDate(e.target.value)
                                    }
                                    className="w-full rounded border border-neutral-200 px-3 py-2 text-sm text-neutral-800 outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-200"
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs text-neutral-500 mb-1">
                                  Order Note
                                </label>
                                <textarea
                                  value={orderNote}
                                  onChange={(e) => setOrderNote(e.target.value)}
                                  placeholder="Add a note for this order (optional)"
                                  rows={3}
                                  className="w-full resize-none rounded border border-neutral-200 px-3 py-2 text-sm text-neutral-800 outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-200"
                                />
                              </div>
                            </div>

                            <button
                              type="button"
                              disabled={
                                stock.disabled ||
                                submittingOrder ||
                                !orderQuantity ||
                                !expectedDeliveryDate ||
                                !selectedAddress?.id
                              }
                              onClick={handleProductOrder}
                              className="h-11 w-full rounded bg-[#33145f] text-base font-medium text-white transition hover:bg-[#2a0f51] disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:text-neutral-500"
                            >
                              {submittingOrder
                                ? "Submitting..."
                                : "Proceed to Order"}
                            </button>
                          </>
                        );
                      })()}
                  </div>
                </Modal>

                <Search>
                  <div className="search-wrapper w-full">
                    <input
                      className="search-input"
                      type="text"
                      placeholder="Search products"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </Search>
                {inStockLoading && (
                  <p className="py-6 text-center text-sm text-neutral-500">
                    Loading products...
                  </p>
                )}
                {!inStockLoading && products.length === 0 && (
                  <p className="py-6 text-center text-sm text-neutral-500">
                    No products in stock at the moment.
                  </p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {products
                    .filter((p) => {
                      if (!searchTerm) return true;
                      const term = searchTerm.toLowerCase();
                      return (
                        p.name.toLowerCase().includes(term) ||
                        p.company.toLowerCase().includes(term) ||
                        p.batchNo.toLowerCase().includes(term)
                      );
                    })
                    .map((product) => (
                      <OrderByProductSearch
                        key={product.id}
                        name={product.name}
                        company={product.company}
                        batchNo={product.batchNo}
                        expiryDate={product.expiryDate}
                        available={product.available}
                        imageUrl={product.imageUrl}
                        status={product.status}
                        unitPrice={product.unitPrice}
                        packPrice={product.packPrice}
                        cartonPrice={product.cartonPrice}
                        unitsPerPack={product.unitsPerPack}
                        packsPerCarton={product.packsPerCarton}
                        onOrder={() => {
                          const rawItem = (inStockProducts as any[]).find(
                            (i: any) => i.id === product.id,
                          );
                          setSelectedRawItem(rawItem ?? null);
                          setSelectedProduct(product);
                          setShowProductPreview(true);
                        }}
                      />
                    ))}
                </div>
                <Pagination
                  onNextPage={handleProductNextPage}
                  onPerPageChange={handleProductPerPage}
                  pageCount={productPageCount}
                />
              </>
            )}
        </div>
      </div>
    </RequisitionWrapper>
  );
}
